import { MapPin, Clock, Phone, MessageCircle } from "lucide-react";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import SalonCTA, { SALON_PHONE_DISPLAY, SALON_PHONE_TEL } from "@/components/SalonCTA";
import SalonServices from "@/components/salon/SalonServices";
import { WHATSAPP_NUMBER } from "@/lib/socialLinks";
import salonHeroImg from "@/assets/salon/salon-1.jpg.asset.json";
import salonShopfrontImg from "@/assets/salon/salon-shopfront.jpg.asset.json";

const SALON_ADDRESS = {
  street: "21 Market Avenue",
  city: "Plymouth",
  postcode: "PL1 1PG",
  country: "GB",
};

const OPENING_HOURS = [
  { day: "Monday", hours: "9:30 – 17:30" },
  { day: "Tuesday", hours: "9:30 – 17:30" },
  { day: "Wednesday", hours: "9:30 – 17:30" },
  { day: "Thursday", hours: "9:30 – 19:00" },
  { day: "Friday", hours: "9:30 – 17:30" },
  { day: "Saturday", hours: "9:30 – 17:00" },
  { day: "Sunday", hours: "Closed" },
];


const buildWhatsappUrl = (message: string) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

const mapEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(
  `${SALON_ADDRESS.street}, ${SALON_ADDRESS.city} ${SALON_ADDRESS.postcode}`
)}&output=embed`;

const SalonPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Plymhair Salon — Plymouth Wigs & Extensions"
        description="Visit Plymhair Salon in Plymouth for private wig fittings, hair extensions, NHS appointments and consultations. Book on WhatsApp or call us today."
        url="/salon"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "HairSalon",
          name: "Plymhair Salon",
          description:
            "Plymouth hair salon specialising in wig fittings, hair extensions, NHS wig appointments and consultations.",
          telephone: SALON_PHONE_TEL,
          email: "sales@plymhair.co.uk",
          image: "https://www.plymhair.co.uk/plymhair-logo.svg",
          url: "https://www.plymhair.co.uk/salon",
          priceRange: "££",
          address: {
            "@type": "PostalAddress",
            streetAddress: SALON_ADDRESS.street,
            addressLocality: SALON_ADDRESS.city,
            postalCode: SALON_ADDRESS.postcode,
            addressCountry: SALON_ADDRESS.country,
          },
          openingHoursSpecification: [
            { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Friday"], opens: "09:30", closes: "17:30" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: "Thursday", opens: "09:30", closes: "19:00" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:30", closes: "17:00" },
          ],
          sameAs: [
            "https://www.instagram.com/plymhair/",
            "https://www.facebook.com/plymhairextensions/",
            "https://www.tiktok.com/@plymhair",
          ],
        }}
      />
      <Ticker />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-foreground text-primary-foreground overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${salonHeroImg.url})` }}
        />
        <div aria-hidden className="absolute inset-0 bg-foreground/60" />
        <div className="relative container max-w-4xl text-center py-20 sm:py-24 md:py-32 lg:py-36 px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-sm text-primary px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-body font-semibold mb-5 md:mb-6">
            <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Plymouth City Centre
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-5 md:mb-6">
            The Plymhair Salon —{" "}
            <span className="italic font-normal block sm:inline">in person, in Plymouth.</span>
          </h1>
          <p className="font-body text-primary-foreground/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-7 md:mb-8">
            Private wig fittings, extension installs, NHS appointments and
            consultations — all under one roof with an experienced specialist
            who treats every visit with care.
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-3">
            <a
              href={buildWhatsappUrl("Hi Plymhair — I'd like to book an appointment at the Plymouth salon.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-body text-sm font-semibold tracking-wider uppercase px-6 sm:px-7 py-3.5 rounded-lg hover:bg-primary/90 transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Book on WhatsApp
            </a>
            <a
              href={`tel:${SALON_PHONE_TEL}`}
              className="inline-flex items-center justify-center gap-2 border border-primary-foreground/30 text-primary-foreground font-body text-sm font-semibold tracking-wider uppercase px-6 sm:px-7 py-3.5 rounded-lg hover:bg-primary-foreground/10 transition-colors"
            >
              <Phone className="w-4 h-4" /> {SALON_PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <SalonServices />


      {/* Find us */}
      <section className="py-14 sm:py-16 md:py-24 bg-secondary">
        <div className="container max-w-5xl px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 md:gap-10 lg:gap-14 items-start">
            <div className="order-2 md:order-1">
              <h2 className="font-display text-3xl sm:text-4xl md:text-4xl font-bold mb-5 md:mb-6">
                Find us
              </h2>

              <div className="space-y-5 font-body">
                <div className="flex gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">{SALON_ADDRESS.street}</p>
                    <p className="text-muted-foreground">
                      {SALON_ADDRESS.city} {SALON_ADDRESS.postcode}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Phone className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <a
                    href={`tel:${SALON_PHONE_TEL}`}
                    className="hover:text-primary transition-colors"
                  >
                    {SALON_PHONE_DISPLAY}
                  </a>
                </div>

                <div className="flex gap-3">
                  <Clock className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <ul className="space-y-1 text-sm w-full max-w-xs">
                    {OPENING_HOURS.map(({ day, hours }) => (
                      <li key={day} className="flex justify-between gap-4 sm:gap-8">
                        <span className="text-muted-foreground">{day}</span>
                        <span>{hours}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2 space-y-4">
              <div className="rounded-2xl overflow-hidden border border-border">
                <img
                  src={salonShopfrontImg.url}
                  alt="Plymhair Salon shopfront at 21 Market Avenue, Plymouth"
                  loading="lazy"
                  className="w-full h-auto object-cover aspect-[3/2]"
                />
              </div>
              <div className="rounded-2xl overflow-hidden border border-border min-h-[240px] sm:min-h-[260px]">
                <iframe
                  src={mapEmbedSrc}
                  title="Plymhair Salon location in Plymouth"
                  className="w-full h-full min-h-[260px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20">
        <div className="container max-w-4xl">
          <SalonCTA />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SalonPage;
